import { Dataset, DtSort } from '@/components/data-table/helpers';
import { CreatePartnerModel, PartnerModel } from '../models/partner.model';
import { JsonObject } from '../helpers';
import { CreateResponse, DeleteResponse, PatchResponse } from '.';
import { create, deleteApi, get, patch } from './sp-api.service';
import { orderBy } from 'lodash';
import { AuthProvider } from './auth.service';

const SAFE_LIMIT = 250;

type ListResponse = {
  count: number;
  currentPage: number;
  data: JsonObject[];
  hasNext: boolean;
  hasPrevious: boolean;
  pageSize: number;
  totalPages: number;
};

export const getPartner = async (): Promise<PartnerModel | null> => {
  return AuthProvider.partnerId ? getPartnerById(AuthProvider.partnerId) : null;
};

export const getPartnerById = async (partnerId: string): Promise<PartnerModel | null> => {
  const response = await get(`/partners/${partnerId}`);
  return response ? new PartnerModel(response as JsonObject) : null;
};

export const getPartnersByIds = async (partnerIds: string[]): Promise<PartnerModel[]> => {
  const allPartners = await getAllPartners();
  return allPartners.filter(({ id }) => partnerIds.includes(id));
};

export const getAllPartners = async (): Promise<PartnerModel[]> => {
  const { rows } = await getPartners(0, SAFE_LIMIT, { columnName: 'name', direction: 'asc' });
  return rows;
};

export const getPartners = async (
  page: number,
  pageSize: number,
  dtSort?: DtSort
): Promise<Dataset<PartnerModel>> => {
  const response = (await get('/partners', {
    page: 1,
    pageSize: SAFE_LIMIT,
  })) as ListResponse;

  if (!response) {
    return {
      page,
      pageSize,
      rows: [],
      total: 0,
    };
  }

  const { data, count } = response;

  let rows = data.map((data) => new PartnerModel(data));

  // Sort
  if (dtSort) {
    const { columnName, direction } = dtSort;
    rows = orderBy(
      rows,
      [(partner: PartnerModel) => String(partner[columnName as keyof PartnerModel]).toLowerCase()],
      [direction]
    );
  }

  // Paginate
  const startIndex = page * pageSize;
  rows = rows.slice(startIndex, startIndex + pageSize);

  return Promise.resolve({
    page,
    pageSize,
    rows,
    total: count,
  });
};

export const patchPartner = async (partner: PartnerModel, isSuper = false): Promise<PatchResponse> => {
  const createPartnerModel = new CreatePartnerModel(partner, isSuper);
  return patch(`/partners/${partner.id}`, createPartnerModel._props);
};

export const deletePartner = async (partnerId: string): Promise<DeleteResponse> => {
  return deleteApi(`/partners/${partnerId}`);
};

export const createPartner = async (partner: PartnerModel, isSuper = false): Promise<CreateResponse> => {
  const createPartnerModel = new CreatePartnerModel(partner, isSuper);
  const response = await create('/partners', createPartnerModel._props);
  if (!response.created) {
    return response;
  }
  response.model = new PartnerModel(response.data);
  return response;
};
